import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Star, Send, CheckCircle, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceOptions = [
  "Roofing",
  "Painting",
  "Window & Doors",
  "Home Additions",
  "Fire & Water Restoration",
  "Bonus Rooms",
  "Driveways",
  "Fences & More",
  "Other",
];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: "",
    address: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service_type) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Quote request submitted! We'll be in touch soon.");
      setSubmitted(true);
    } catch (err) {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Header */}
      <section className="bg-brand-dark py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="font-outfit text-sm font-bold tracking-[0.2em] uppercase text-brand block mb-3">
              Get In Touch
            </span>
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase text-white">
              Contact Us
            </h1>
            <p className="font-dm-sans text-neutral-400 text-base mt-4 max-w-lg">
              Ready to start your project? Fill out the form below for a free
              quote, or call us directly.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 md:gap-16">
            {/* Form */}
            <div className="lg:col-span-3">
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-16 border border-neutral-200 p-8"
                  data-testid="quote-success"
                >
                  <CheckCircle className="w-16 h-16 text-brand mx-auto mb-6" />
                  <h2 className="font-outfit text-2xl font-bold text-brand-dark mb-3">
                    Quote Request Received!
                  </h2>
                  <p className="font-dm-sans text-neutral-600 mb-6">
                    Thank you for reaching out. Mario will get back to you
                    within 24 hours.
                  </p>
                  <Button
                    onClick={() => {
                      setSubmitted(false);
                      setForm({
                        name: "",
                        email: "",
                        phone: "",
                        service_type: "",
                        address: "",
                        message: "",
                      });
                    }}
                    className="bg-brand text-white font-outfit font-bold uppercase tracking-wider text-sm px-8 py-5 hover:bg-brand-hover rounded-none"
                  >
                    Submit Another Request
                  </Button>
                </motion.div>
              ) : (
                <motion.form
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  onSubmit={handleSubmit}
                  data-testid="contact-form"
                  className="space-y-6"
                >
                  <div>
                    <h2 className="font-outfit text-2xl font-bold tracking-tight text-brand-dark mb-1">
                      Request a Free Quote
                    </h2>
                    <p className="font-dm-sans text-sm text-neutral-500">
                      Fields marked with * are required
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label
                        htmlFor="name"
                        className="font-outfit font-semibold text-sm uppercase tracking-wider text-brand-dark"
                      >
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        data-testid="input-name"
                        className="rounded-none border-2 border-neutral-300 focus:border-brand focus:ring-brand py-5 font-dm-sans"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="email"
                        className="font-outfit font-semibold text-sm uppercase tracking-wider text-brand-dark"
                      >
                        Email *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        data-testid="input-email"
                        className="rounded-none border-2 border-neutral-300 focus:border-brand focus:ring-brand py-5 font-dm-sans"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label
                        htmlFor="phone"
                        className="font-outfit font-semibold text-sm uppercase tracking-wider text-brand-dark"
                      >
                        Phone Number *
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="(901) 000-0000"
                        data-testid="input-phone"
                        className="rounded-none border-2 border-neutral-300 focus:border-brand focus:ring-brand py-5 font-dm-sans"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="service"
                        className="font-outfit font-semibold text-sm uppercase tracking-wider text-brand-dark"
                      >
                        Service Needed *
                      </Label>
                      <Select
                        value={form.service_type}
                        onValueChange={(val) =>
                          setForm({ ...form, service_type: val })
                        }
                      >
                        <SelectTrigger
                          data-testid="select-service"
                          className="rounded-none border-2 border-neutral-300 focus:border-brand focus:ring-brand py-5 font-dm-sans"
                        >
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {serviceOptions.map((opt) => (
                            <SelectItem
                              key={opt}
                              value={opt}
                              className="font-dm-sans"
                            >
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="address"
                      className="font-outfit font-semibold text-sm uppercase tracking-wider text-brand-dark"
                    >
                      Project Address
                    </Label>
                    <Input
                      id="address"
                      name="address"
                      value={form.address}
                      onChange={handleChange}
                      placeholder="Street address, City, TN"
                      data-testid="input-address"
                      className="rounded-none border-2 border-neutral-300 focus:border-brand focus:ring-brand py-5 font-dm-sans"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="message"
                      className="font-outfit font-semibold text-sm uppercase tracking-wider text-brand-dark"
                    >
                      Project Details
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project..."
                      rows={5}
                      data-testid="input-message"
                      className="rounded-none border-2 border-neutral-300 focus:border-brand focus:ring-brand font-dm-sans resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="submit-quote-btn"
                    className="w-full bg-brand text-white font-outfit font-bold uppercase tracking-wider text-sm py-6 hover:bg-brand-hover rounded-none disabled:opacity-50"
                  >
                    {loading ? (
                      "Submitting..."
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Quote Request
                      </>
                    )}
                  </Button>
                </motion.form>
              )}
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="space-y-8"
              >
                <div className="bg-brand-dark p-8 text-white">
                  <h3 className="font-outfit text-xl font-bold uppercase tracking-wider mb-6">
                    Contact Information
                  </h3>
                  <div className="space-y-5">
                    <a
                      href="tel:9014061803"
                      className="flex items-center gap-4 group"
                      data-testid="contact-phone"
                    >
                      <div className="w-10 h-10 bg-brand flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <span className="font-dm-sans text-xs text-neutral-400 block">
                          Phone
                        </span>
                        <span className="font-dm-sans font-semibold group-hover:text-brand transition-colors">
                          (901) 406-1803
                        </span>
                      </div>
                    </a>
                    <a
                      href="mailto:Masb254@yahoo.com"
                      className="flex items-center gap-4 group"
                      data-testid="contact-email"
                    >
                      <div className="w-10 h-10 bg-brand flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <span className="font-dm-sans text-xs text-neutral-400 block">
                          Email
                        </span>
                        <span className="font-dm-sans font-semibold group-hover:text-brand transition-colors">
                          Masb254@yahoo.com
                        </span>
                      </div>
                    </a>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-brand flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <span className="font-dm-sans text-xs text-neutral-400 block">
                          Location
                        </span>
                        <span className="font-dm-sans font-semibold">
                          Memphis, TN
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-brand flex items-center justify-center flex-shrink-0">
                        <Clock className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <span className="font-dm-sans text-xs text-neutral-400 block">
                          Business Hours
                        </span>
                        <span className="font-dm-sans font-semibold">
                          Mon-Sat: 7AM - 6PM
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Financing Badge */}
                <div className="bg-brand p-6 text-white text-center" data-testid="financing-badge">
                  <Star className="w-8 h-8 mx-auto mb-3 fill-white" />
                  <h4 className="font-outfit font-bold text-lg uppercase tracking-wider mb-2">
                    Financing Available
                  </h4>
                  <p className="font-dm-sans text-sm text-white/80">
                    Ask about our flexible financing options to make your project
                    affordable.
                  </p>
                </div>

                {/* Direct Call CTA */}
                <div className="border-2 border-neutral-200 p-6 text-center">
                  <h4 className="font-outfit font-bold text-lg text-brand-dark mb-2">
                    Prefer to Talk?
                  </h4>
                  <p className="font-dm-sans text-sm text-neutral-500 mb-4">
                    Call Mario directly for immediate assistance
                  </p>
                  <a href="tel:9014061803">
                    <Button className="w-full bg-brand-dark text-white font-outfit font-bold uppercase tracking-wider text-sm py-5 hover:bg-neutral-800 rounded-none">
                      <Phone className="w-4 h-4 mr-2" />
                      (901) 406-1803
                    </Button>
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* GOOGLE MAPS */}
      <section className="bg-neutral-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <span className="font-outfit text-sm font-bold tracking-[0.2em] uppercase text-brand block mb-3">
              Service Area
            </span>
            <h2 className="font-outfit text-2xl sm:text-3xl font-bold tracking-tight uppercase text-brand-dark">
              Serving Memphis & Surrounding Areas
            </h2>
          </motion.div>
          <div className="w-full h-80 md:h-96 border-2 border-neutral-200">
            <iframe
              title="Mid America Star Builders Service Area"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d209698.3612525055!2d-90.18480765!3d35.1175895!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x87d57e1eea439745%3A0xd193f315601ab6fe!2sMemphis%2C%20TN!5e0!3m2!1sen!1sus!4v1700000000000!5m2!1sen!1sus"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="font-outfit text-sm font-bold tracking-[0.2em] uppercase text-brand block mb-3">
              Common Questions
            </span>
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold tracking-tight uppercase text-brand-dark">
              FAQ
            </h2>
          </motion.div>
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-section">
            {[
              {
                q: "Do you offer free estimates?",
                a: "Yes! We provide free, no-obligation estimates for all our services. Contact us by phone or fill out our quote request form and we'll get back to you within 24 hours.",
              },
              {
                q: "What areas do you serve?",
                a: "We serve Memphis, TN and surrounding areas including Germantown, Collierville, Bartlett, Cordova, Arlington, Lakeland, and more.",
              },
              {
                q: "Are you licensed and insured?",
                a: "Absolutely. Mid America Star Builders LLC is fully licensed and insured in the state of Tennessee. We carry general liability insurance and workers' compensation for your protection.",
              },
              {
                q: "How does financing work?",
                a: "We offer flexible financing options to help make your project affordable. Ask Mario about our financing plans when you request your free estimate — we'll find a payment solution that works for your budget.",
              },
              {
                q: "How long does a typical project take?",
                a: "Project timelines vary depending on scope. A roof replacement typically takes 1-3 days, painting projects 2-5 days, and larger renovations or additions can take several weeks. We'll provide a detailed timeline with your estimate.",
              },
              {
                q: "Do you handle insurance claims?",
                a: "Yes, we work with all major insurance companies for fire and water restoration projects. We can help guide you through the claims process to ensure your project is covered.",
              },
              {
                q: "What is your payment structure?",
                a: "We require a one-third (1/3) deposit to begin work. The remaining balance is due upon project completion. We accept checks, cash, and can discuss financing options.",
              },
              {
                q: "Can I see examples of your work?",
                a: "Of course! Visit our Gallery page to see photos of completed projects, or ask us for references from satisfied customers in your area.",
              },
            ].map((item, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border-2 border-neutral-200 px-6 data-[state=open]:border-brand"
              >
                <AccordionTrigger className="font-outfit font-bold text-brand-dark text-left text-sm hover:text-brand hover:no-underline py-5">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="font-dm-sans text-neutral-600 text-sm leading-relaxed pb-5">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
